import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';

import {
  missingPasswordRequirements,
  PASSWORD_MIN_LENGTH,
  PASSWORD_REQUIREMENTS,
  PASSWORD_SYMBOLS,
  PasswordRequirement,
  passwordsMatch,
} from 'config/passwordRules';

/** 条件の文言の項目（login の password-rules）。 */
const LABEL_KEYS: Record<PasswordRequirement, string> = {
  invisible: 'invisible',
  halfWidth: 'half-width',
  minLength: 'min-length',
  lowercase: 'lowercase',
  uppercase: 'uppercase',
  digit: 'digit',
  symbol: 'symbol',
};

const VALUES = { length: PASSWORD_MIN_LENGTH, symbols: PASSWORD_SYMBOLS };

interface PasswordRequirementsProps {
  password: string;
  /** 確認用の欄の値。渡したときだけ「確認用パスワードと一致する」を並べる。 */
  confirm?: string;
  id?: string;
}

/**
 * パスワードの条件の一覧（2026-10-26、EV-474）。条件ごとに、満たしたかどうかを印と色と文字で示す。
 * 判定は config/passwordRules（サーバー・アプリと同じ規則）。アプリの PasswordRequirementsChecklist と同じ並び。
 */
const PasswordRequirements: React.FC<PasswordRequirementsProps> = ({ password, confirm, id }) => {
  const { t } = useTranslation('login');
  const missing = missingPasswordRequirements(password);
  const rows: { key: string; label: string; met: boolean }[] = PASSWORD_REQUIREMENTS.map((requirement) => ({
    key: requirement,
    label: t(`password-rules.${LABEL_KEYS[requirement]}`, VALUES),
    met: !missing.includes(requirement),
  }));
  if (confirm !== undefined) {
    rows.push({ key: 'confirm', label: t('password-rules.confirm-match'), met: passwordsMatch(password, confirm) });
  }
  return (
    <div id={id} className="flex flex-col gap-1 text-left" data-testid="password-requirements">
      <p className="text-caption text-neutral/300">{t('password-rules.title')}</p>
      <ul className="flex flex-col gap-1">
        {rows.map((row) => (
          <li
            key={row.key}
            data-requirement={row.key}
            data-met={row.met ? 'true' : 'false'}
            className={`flex items-start gap-2 text-caption ${row.met ? 'text-green-700' : 'text-red-700'}`}
          >
            {row.met ? (
              <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            ) : (
              <XCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            )}
            <span>
              {row.label}
              <span className="sr-only">{` ${row.met ? t('password-rules.met') : t('password-rules.unmet')}`}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * 送る前の確かめ。満たしていない条件があれば、その名前を並べた文（例「パスワードの条件を満たしていません（大文字、記号）。」）。
 * 満たしていれば null。以前は「大文字、数字、記号を含む 8 文字」とだけ出し、どれが足りないか分からなかった。
 */
export function usePasswordRuleError(): (password: string) => string | null {
  const { t } = useTranslation('login');
  return useCallback(
    (password: string) => {
      const missing = missingPasswordRequirements(password);
      if (missing.length === 0) return null;
      const items = missing
        .map((requirement) => t(`password-rules.name-${LABEL_KEYS[requirement]}`, VALUES))
        .join(t('password-rules.separator'));
      return t('password-rules.error-unmet', { items });
    },
    [t]
  );
}

export default PasswordRequirements;
